// Player movement + collision. Tuned for tight, responsive feel: velocity
// tracks input almost instantly (minimal inertia), per-axis circle/grid
// collision lets you slide along walls, and turning comes from keys OR relative
// pointer deltas (mouse-style aim without needing pointer-lock in core input).

import type { InputController } from "../../core/input";
import { TAU, clamp, normalizeAngle } from "./math";
import { isSolid } from "./types";
import type { Level } from "./types";

const RADIUS = 0.22;
const MOVE_SPEED = 3.3; // cells/sec
const TURN_SPEED = 2.7; // rad/sec (keyboard)
const POINTER_GAIN = 0.0042; // rad per logical pixel of pointer movement
const ACCEL = 18; // velocity smoothing toward target

function blocked(level: Level, x: number, y: number, r: number): boolean {
  const minX = Math.floor(x - r);
  const maxX = Math.floor(x + r);
  const minY = Math.floor(y - r);
  const maxY = Math.floor(y + r);
  for (let cy = minY; cy <= maxY; cy++) {
    for (let cx = minX; cx <= maxX; cx++) {
      if (!isSolid(level, cx, cy)) continue;
      const nearestX = clamp(x, cx, cx + 1);
      const nearestY = clamp(y, cy, cy + 1);
      const dx = x - nearestX;
      const dy = y - nearestY;
      if (dx * dx + dy * dy < r * r) return true;
    }
  }
  return false;
}

export class Player {
  x: number;
  y: number;
  angle: number;
  health = 100;
  armor = 25;
  private vx = 0;
  private vy = 0;
  private lastPointerX: number | null = null;

  constructor(start: { x: number; y: number; angle: number }) {
    this.x = start.x;
    this.y = start.y;
    this.angle = start.angle;
  }

  /** Advance movement for one fixed step. Returns distance moved (for headbob/audio). */
  update(input: InputController, dt: number, level: Level): number {
    // --- Turn ---
    let turn = 0;
    const strafing = input.isDown("secondary");
    if (!strafing) {
      if (input.isDown("left")) turn -= 1;
      if (input.isDown("right")) turn += 1;
    }
    this.angle += turn * TURN_SPEED * dt;

    if (input.pointer.active) {
      if (this.lastPointerX !== null) {
        this.angle += (input.pointer.x - this.lastPointerX) * POINTER_GAIN;
      }
      this.lastPointerX = input.pointer.x;
    } else {
      this.lastPointerX = null;
    }
    this.angle = normalizeAngle(this.angle);

    // --- Desired movement (forward/back + strafe) ---
    const dirX = Math.cos(this.angle);
    const dirY = Math.sin(this.angle);
    let fwd = 0;
    if (input.isDown("up")) fwd += 1;
    if (input.isDown("down")) fwd -= 1;
    let strafe = 0;
    if (strafing) {
      if (input.isDown("left")) strafe -= 1;
      if (input.isDown("right")) strafe += 1;
    }

    let tx = (dirX * fwd + -dirY * strafe) * MOVE_SPEED;
    let ty = (dirY * fwd + dirX * strafe) * MOVE_SPEED;
    const mag = Math.hypot(tx, ty);
    if (mag > MOVE_SPEED) {
      tx = (tx / mag) * MOVE_SPEED;
      ty = (ty / mag) * MOVE_SPEED;
    }

    // Smooth velocity toward target for responsive but non-jittery motion.
    const k = Math.min(1, ACCEL * dt);
    this.vx += (tx - this.vx) * k;
    this.vy += (ty - this.vy) * k;

    // Per-axis collision so we slide along walls.
    const nx = this.x + this.vx * dt;
    if (!blocked(level, nx, this.y, RADIUS)) this.x = nx;
    else this.vx = 0;
    const ny = this.y + this.vy * dt;
    if (!blocked(level, this.x, ny, RADIUS)) this.y = ny;
    else this.vy = 0;

    return Math.hypot(this.vx, this.vy) * dt;
  }

  damage(amount: number): void {
    // Armor absorbs a third, Doom-style.
    const absorbed = Math.min(this.armor, amount * 0.33);
    this.armor -= absorbed;
    this.health = clamp(this.health - (amount - absorbed), 0, 100);
  }

  get dir(): { x: number; y: number } {
    return { x: Math.cos(this.angle), y: Math.sin(this.angle) };
  }

  static readonly TAU = TAU;
}
